import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Autocomplete,
  FormHelperText,
  CircularProgress,
  Collapse
} from '@mui/material';
import { AuthContext } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  Timestamp,
  collectionGroup
} from 'firebase/firestore';
import { 
  CONTACT_TYPES, 
  ENTITY_TYPES, 
  MEETING_OUTCOMES 
} from '../../constants/contactsConstants';

const ContactForm = ({ initialData = null, onSubmit, onCancel, preselectedEntity = null, clients = [], leads = [] }) => {
  const { currentUser } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Stan formularza
  const [formData, setFormData] = useState({
    entityType: preselectedEntity?.type || 'client',
    entityId: preselectedEntity?.id || '',
    employeeId: currentUser?.uid || '',
    contactType: 'phone_call',
    contactDate: new Date().toISOString().split('T')[0],
    contactTime: new Date().toTimeString().slice(0, 5),
    notes: '',
    scheduledMeetingDate: '',
    scheduledMeetingTime: '',
    meetingOutcome: ''
  });

  // Jeśli mamy dane początkowe (edycja), załaduj je
  useEffect(() => {
    if (initialData) {
      const contactDate = initialData.contactDate instanceof Date 
        ? initialData.contactDate 
        : initialData.contactDate.toDate();
      
      setFormData({
        entityType: initialData.entityType || 'client',
        entityId: initialData.entityId || '',
        employeeId: initialData.employeeId || currentUser?.uid || '',
        contactType: initialData.contactType || 'phone_call',
        contactDate: contactDate.toISOString().split('T')[0],
        contactTime: contactDate.toTimeString().slice(0, 5),
        notes: initialData.notes || '',
        scheduledMeetingDate: initialData.scheduledMeetingDate || '',
        scheduledMeetingTime: initialData.scheduledMeetingTime || '',
        meetingOutcome: initialData.meetingOutcome || ''
      });
    }
  }, [initialData]);



  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Wyczyść błąd dla tego pola
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  const handleEntitySelect = (event, value) => {
    if (value) {
      setFormData({
        ...formData,
        entityId: value.id
      });
      
      if (errors.entityId) {
        setErrors({
          ...errors,
          entityId: null
        });
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.entityId) {
      newErrors.entityId = 'Wybierz klienta lub leada';
    }
    
    if (!formData.notes.trim()) {
      newErrors.notes = 'Notatka jest wymagana';
    }
    
    if (formData.contactType === 'in_person' && !formData.scheduledMeetingDate) {
      newErrors.scheduledMeetingDate = 'Data spotkania jest wymagana';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Przygotuj dane do zapisania
      const contactDate = new Date(`${formData.contactDate}T${formData.contactTime || '00:00'}`);
      
      const contactData = {
        entityType: formData.entityType,
        entityId: formData.entityId,
        employeeId: formData.employeeId,
        contactType: formData.contactType,
        contactDate: contactDate,
        notes: formData.notes,
        createdAt: new Date()
      };
      
      // Dodaj pola związane ze spotkaniem, jeśli to spotkanie
      if (formData.contactType === 'in_person') {
        contactData.scheduledMeetingDate = formData.scheduledMeetingDate;
        contactData.scheduledMeetingTime = formData.scheduledMeetingTime;
        contactData.meetingOutcome = formData.meetingOutcome || null;
      }
      
      let result;
      
      // Zapisz w odpowiedniej kolekcji w zależności od typu podmiotu
      if (formData.entityType === 'client') {
        // Zapisz w kolekcji contacts
        const contactsRef = collection(db, 'contacts');
        result = await addDoc(contactsRef, contactData);
      } else if (formData.entityType === 'lead') {
        // Zapisz w subkolekcji leads/{leadId}/comments
        const commentsRef = collection(db, 'leads', formData.entityId, 'comments');
        result = await addDoc(commentsRef, contactData);
      }
      
      // Wywołaj callback z danymi
      if (onSubmit) {
        onSubmit({
          id: result.id,
          ...contactData
        });
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Błąd podczas zapisywania kontaktu:', error);
      setLoading(false);
    }
  };

  // Przygotowanie opcji dla autocomplete
  const getEntityOptions = () => {
    if (formData.entityType === 'client') {
      return clients.map(client => ({
        id: client.id,
        label: `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.id
      }));
    } else {
      return leads.map(lead => ({
        id: lead.id,
        label: `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.id
      }));
    }
  };

  // Znajdź wybraną opcję dla autocomplete
  const getSelectedEntity = () => {
    if (!formData.entityId) return null;
    
    const options = getEntityOptions();
    return options.find(option => option.id === formData.entityId) || null;
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Typ podmiotu (klient/lead) */}
        {!preselectedEntity && (
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Typ podmiotu</InputLabel>
              <Select
                name="entityType"
                value={formData.entityType}
                onChange={handleInputChange}
                label="Typ podmiotu"
                disabled={!!preselectedEntity}
              >
                {Object.entries(ENTITY_TYPES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Wybór konkretnego klienta/leada */}
        <Grid item xs={12} md={preselectedEntity ? 12 : 6}>
          <Autocomplete
            value={getSelectedEntity()}
            options={getEntityOptions().sort((a, b) => a.name.localeCompare(b.name))}
            onChange={handleEntitySelect}
            disabled={!!preselectedEntity}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={formData.entityType === 'client' ? 'Wybierz klienta' : 'Wybierz leada'} 
                placeholder="Wyszukaj..."
                fullWidth
                error={!!errors.entityId}
                helperText={errors.entityId}
              />
            )}
          />
        </Grid>

        {/* Typ kontaktu */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Typ kontaktu</InputLabel>
            <Select
              name="contactType"
              value={formData.contactType}
              onChange={handleInputChange}
              label="Typ kontaktu"
            >
              {Object.entries(CONTACT_TYPES).map(([key, value]) => (
                <MenuItem key={key} value={key}>{value}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Data kontaktu */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Data kontaktu"
            name="contactDate"
            type="date"
            value={formData.contactDate}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Czas kontaktu */}
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            label="Czas kontaktu"
            name="contactTime"
            type="time"
            value={formData.contactTime}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Notatki */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Notatki"
            name="notes"
            multiline
            rows={4}
            value={formData.notes}
            onChange={handleInputChange}
            error={!!errors.notes}
            helperText={errors.notes}
          />
        </Grid>

        {/* Sekcja spotkania (widoczna tylko dla typu "in_person") */}
        <Grid item xs={12}>
          <Collapse in={formData.contactType === 'in_person'}>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2, mt: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                Szczegóły spotkania
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data spotkania"
                    name="scheduledMeetingDate"
                    type="date"
                    value={formData.scheduledMeetingDate}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.scheduledMeetingDate}
                    helperText={errors.scheduledMeetingDate}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Czas spotkania"
                    name="scheduledMeetingTime"
                    type="time"
                    value={formData.scheduledMeetingTime}
                    onChange={handleInputChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Wynik spotkania</InputLabel>
                    <Select
                      name="meetingOutcome"
                      value={formData.meetingOutcome}
                      onChange={handleInputChange}
                      label="Wynik spotkania"
                    >
                      <MenuItem value="">Brak wyniku</MenuItem>
                      {Object.entries(MEETING_OUTCOMES).map(([key, value]) => (
                        <MenuItem key={key} value={key}>{value}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      Możesz uzupełnić później, po odbyciu spotkania
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Grid>

        {/* Przyciski */}
        <Grid item xs={12} sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={onCancel}
            disabled={loading}
          >
            Anuluj
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Zapisz kontakt'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactForm;
